import { Injectable } from "@angular/core";
import {
  addDoc,
  collection,
  collectionData,
  doc,
  DocumentData,
  Firestore,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  QueryDocumentSnapshot,
  startAfter,
  startAt,
  updateDoc,
  where,
} from "@angular/fire/firestore";
import { from, map, Observable, of } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class CompanyService {
  private itemsPerPage = 5;
  private lastDoc: QueryDocumentSnapshot<DocumentData> | null = null;
  private firstDoc: QueryDocumentSnapshot<DocumentData> | null = null;
  private lastVisibleDocs: QueryDocumentSnapshot<DocumentData>[] = [];
  hasNextPage = false;
  hasPreviousPage = false;

  private collectionRef;
  private collectionName = "companies";
  private fieldSort = "nomeFantasia";

  constructor(private firestore: Firestore) {
    this.collectionRef = collection(this.firestore, this.collectionName);
  }

  async create(company: any) {
    const docRef = await addDoc(this.collectionRef, company);
    return await updateDoc(docRef, { id: docRef.id });
  }

  async update(id: string, company: any) {
    const docRef = doc(this.firestore, this.collectionName, id);
    return await updateDoc(docRef, company);
  }

  async updatePhoto(id: string, url: any) {
    const docRef = doc(this.firestore, this.collectionName, id);
    return await updateDoc(docRef, {
      foto: url,
    })
      .then(() => console.log("Documento atualizado com sucesso!"))
      .catch((error) => console.error("Erro ao atualizar documento:", error));
  }

  async getCompanyByCNPJ(cnpj: string): Promise<boolean> {
    const collectionRef = collection(this.firestore, this.collectionName);
    const q = query(collectionRef, where("cnpj", "==", cnpj));
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  }

  async getCompanyByEmailAndDocument(email: string, document: string): Promise<any> {
    const normalizedDoc = document.replace(/\D/g, "");
    const emailLower = email.trim().toLowerCase();
    const emailOriginal = email.trim();

    const collectionRef = collection(this.firestore, this.collectionName);

    // Tenta primeiro com e-mail em lowercase, depois com a grafia original
    const emailVariants = Array.from(new Set([emailLower, emailOriginal]));

    for (const emailVariant of emailVariants) {
      const q = query(collectionRef, where("email", "==", emailVariant));
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const match = snapshot.docs.find((d) => {
          const data = d.data();
          const cnpj = (data["cnpj"] ?? "").replace(/\D/g, "");
          const cpf = (data["cpf"] ?? "").replace(/\D/g, "");
          return cnpj === normalizedDoc || cpf === normalizedDoc;
        });
        if (match) return { id: match.id, ...match.data() };
      }
    }

    return null;
  }

  async getCompanyById(id: string): Promise<any> {
    const docRef = doc(this.firestore, this.collectionName, id);
    const snapshot = await getDoc(docRef);
    if (snapshot.exists()) {
      // Garante que o campo "id" sempre reflete o ID real do documento
      return { id: snapshot.id, ...snapshot.data() };
    } else {
      return null;
    }
  }

  getCompanies(): Observable<any[]> {
    return collectionData(this.collectionRef, { idField: "id" }) as Observable<
      any[]
    >;
  }

  getFirstPage(): Observable<DocumentData[]> {
    const colRef = collection(this.firestore, this.collectionName);
    const q = query(
      colRef,
      orderBy(this.fieldSort, "asc"),
      limit(this.itemsPerPage)
    );

    return from(getDocs(q)).pipe(
      map((snapshot) => {
        if (!snapshot.empty) {
          this.firstDoc = snapshot.docs[0];
          this.lastDoc = snapshot.docs[snapshot.docs.length - 1];
          this.lastVisibleDocs = [this.firstDoc];
          this.hasPreviousPage = false;
          this.hasNextPage = snapshot.docs.length === this.itemsPerPage; // Se pegamos o máximo, pode haver mais
        } else {
          this.hasNextPage = false;
          this.hasPreviousPage = false;
        }
        return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      })
    );
  }

  getNextPage(): Observable<DocumentData[]> {
    if (!this.lastDoc) return new Observable();

    const colRef = collection(this.firestore, this.collectionName);
    const q = query(
      colRef,
      orderBy(this.fieldSort, "asc"),
      startAfter(this.lastDoc),
      limit(this.itemsPerPage)
    );

    return from(getDocs(q)).pipe(
      map((snapshot) => {
        if (!snapshot.empty) {
          this.firstDoc = snapshot.docs[0];
          this.lastDoc = snapshot.docs[snapshot.docs.length - 1];
          this.lastVisibleDocs.push(this.firstDoc);
          this.hasPreviousPage = true;
          this.hasNextPage = snapshot.docs.length === this.itemsPerPage;
        } else {
          this.hasNextPage = false;
        }
        return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      })
    );
  }

  getPreviousPage(): Observable<DocumentData[]> {
    if (this.lastVisibleDocs.length < 2) return new Observable();

    const previousDoc = this.lastVisibleDocs[this.lastVisibleDocs.length - 2];
    this.lastVisibleDocs.pop();

    const colRef = collection(this.firestore, this.collectionName);
    const q = query(
      colRef,
      orderBy(this.fieldSort, "asc"),
      startAt(previousDoc),
      limit(this.itemsPerPage)
    );

    return from(getDocs(q)).pipe(
      map((snapshot) => {
        if (!snapshot.empty) {
          this.firstDoc = snapshot.docs[0];
          this.lastDoc = snapshot.docs[snapshot.docs.length - 1];
          this.hasPreviousPage = this.lastVisibleDocs.length > 1;
          this.hasNextPage = true;
        }
        return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      })
    );
  }
}
